// Shared render types, tuning constants, and pure helpers for the Game scene + its controllers.
// Extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT) — behavior-frozen, no logic change.
import Phaser from 'phaser';
import { PALETTE } from '../keys';

// The distinct visual "modes" a (non-wall) cell can be in. Cached on the view so we only redraw
// the (relatively expensive) glyph/border bits when the mode actually changes — armed flicker is
// the only per-frame mutation.
export type CellMode =
  | 'neutral'
  | 'armed' // safe-passed / spent (decoy passed through)
  | 'eatenCorrect'
  | 'eatenDecoy'
  | 'revealCorrect' // scanned, true status currently shown
  | 'revealDecoy';

export interface CellView {
  container: Phaser.GameObjects.Container;
  bg: Phaser.GameObjects.Rectangle;
  /** art tile underlay (only present when tile art is wired) — sits BEHIND bg so text stays on top */
  tileImg?: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  /** Unified state accent border drawn for safe-passed/scanned cells. */
  stateFrame: Phaser.GameObjects.Graphics;
  /** Small dark backing behind compact armed-tile answer text. */
  labelPlate: Phaser.GameObjects.Graphics;
  /** ✕ / ✓ / ✗ status glyph overlaid on the cell corner */
  glyph: Phaser.GameObjects.Text;
  /** centred padlock icon (Graphics) shown on the armed/spent-decoy "locked" state. */
  lockIcon?: Phaser.GameObjects.Graphics;
  w: number;
  h: number;
  mode: CellMode;
  scanned: boolean;
  /** last text the shrink-to-fit ran on (cache so we don't re-measure every frame) */
  fittedText: string;
  /** LIAR TELL: animated glitch overlay (tint band) + a ⚠ warning glyph, shown while `lying`. */
  glitchOverlay: Phaser.GameObjects.Rectangle;
  lieGlyph: Phaser.GameObjects.Text;
  lieTween?: Phaser.Tweens.Tween;
  /**
   * IDLE LIFE: a faint top-edge specular band whose alpha is breathed by a single scene UPDATE handler
   * (phase-offset per cell so the grid shimmers like a slow wave). Sits ABOVE the gel, BELOW the label,
   * so it never touches the answer-text band. Only present under motion (static board has no shimmer).
   */
  idleSheen?: Phaser.GameObjects.Rectangle;
  /** whether the lie tell is currently shown (edge-detect against snapshot.lying). */
  lying: boolean;
  /** last displayText we rendered — drives the SCRAMBLER swap animation on change. */
  lastDisplayText: string;
}

export type InputCmd = 'up' | 'down' | 'left' | 'right' | 'munch' | 'scan';

// --- mobile touch tuning (renderer-only; see the two-touch-input spec) --------------------------
/**
 * Own-cell tap -> CHOMP mis-tap guard (owner playtest 2026-07-02: double-tap-to-CHOMP read as
 * "clanky", so a single own-cell tap now CHOMPs). A rapid movement tap-storm can land a tap on the
 * cell the muncher JUST entered, so an own-cell tap arriving within this window of a pointer-driven
 * move step is swallowed instead of committing an accidental CHOMP. Keyboard Space/Enter and the
 * dock CHOMP button are never gated by this.
 */
export const MOVE_TO_CHOMP_GUARD_MS = 180;
/**
 * Per-step glide cadence for a multi-cell straight drag. Tied to the game's ARR-90 held-direction
 * repeat and >= the ~80ms step-grid, so a drag-glide feels exactly like holding a key. MUST stay
 * >= the ~80ms step-grid; SAFETY does NOT depend on this value (the one-shift-per-frame drain does) —
 * this only governs feel/cadence.
 */
export const STEP_FEED_MS = 90;

/**
 * TEXT-FIT: the largest font size at which setCellLabel may still break a word MID-WORD (advanced
 * wrap) as the last resort before the 9px floor. Above this, only whole-word wrapping is allowed —
 * a smaller intact "Washington DC" always beats a bigger "Washingt/on DC" (store-capture defect).
 */
export const WORD_BREAK_MAX_PX = 11;

/**
 * PER-BOARD ATMOSPHERE (decoration ONLY — Gemini P1 "board 3 identical to board 1"). Each board
 * rung gets a subtle decorative theme: a low-alpha hue wash over the shared backdrop, a tint for
 * the idle drift band / per-cell sheen, and a thin accent on the board frame + NEUTRAL tile
 * borders. Deterministic from boardIndex (no randomness). HARD RULE: gameplay state colors
 * (correct green / hazard red / armed amber / scan cyan / player cyan / rival magenta) are
 * untouchable and every accent below stays desaturated + low-alpha so it can never be mistaken for
 * a state signal — color here DECORATES, it is never an information channel.
 */
export interface BoardTheme {
  /** full-screen backdrop hue wash (NORMAL blend, painted just above the shared backdrop) */
  wash: number;
  washAlpha: number;
  /** decorative accent: board-frame hairline, neutral-tile border blend, sheen/drift-band tint */
  accent: number;
}
export const BOARD_THEMES: readonly BoardTheme[] = [
  { wash: 0x0d1b3d, washAlpha: 0.1, accent: 0x4f83e0 }, // rung 0 — deep blue (closest to the base field)
  { wash: 0x151138, washAlpha: 0.14, accent: 0x818cf8 }, // rung 1 — indigo
  { wash: 0x1d1036, washAlpha: 0.16, accent: 0xa78bfa }, // rung 2 — violet
  { wash: 0x0a2a26, washAlpha: 0.16, accent: 0x5eead4 }, // rung 3 — teal
  { wash: 0x2b130c, washAlpha: 0.16, accent: 0xf0925e }, // rung 4 — ember
];

/** Integer RGB lerp (t=0 -> a, t=1 -> b) for blending theme accents into existing tones. */
export function mixColor(a: number, b: number, t: number): number {
  const r = Math.round(((a >> 16) & 0xff) + ((((b >> 16) & 0xff) - ((a >> 16) & 0xff)) * t));
  const g = Math.round(((a >> 8) & 0xff) + ((((b >> 8) & 0xff) - ((a >> 8) & 0xff)) * t));
  const bl = Math.round((a & 0xff) + (((b & 0xff) - (a & 0xff)) * t));
  return (r << 16) | (g << 8) | bl;
}

/**
 * Deterministic [0,1) hash for MOTIF placement (paintBoardMotif) — a cheap avalanche mix of
 * (rung, elementIndex). NEVER Math.random: the same rung on the same viewport paints the identical
 * field every build/restart, so capture screenshots stay reproducible and nothing re-rolls per board.
 */
export function motifRand(rung: number, i: number): number {
  let h = (Math.imul(rung + 1, 0x9e3779b1) ^ Math.imul(i + 1, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 0x100000000;
}

export interface ComboTier {
  milestone: number;
  label: string;
  hud: string;
  mult: number;
  color: number;
}

export const PLAYER = 0;
export const AI = 1;

// CHOMP SURGE identity (Gemini feel-verify fix): the original hot-orange blended with the armed-amber
// tile borders ("players may fail to realize they are powered up"). Moved to an ELECTRIC blue-white
// crackle: a DEEPER-BLUE core (SURGE_CORE) with a WHITE-HOT edge (SURGE_EDGE) + a sparking particle
// texture, so differentiation is texture+motion, not hue alone. Distinct at a glance from armed-amber,
// scan-cyan (0x38bdf8) / human-cyan (0x22d3ee), rival-magenta, ghost-violet, and safe-green.
export const SURGE_CORE = 0x2a54ff; // deeper electric-blue core glow
export const SURGE_EDGE = 0xeaf3ff; // white-hot ring edge + spark tint

export function comboTier(combo: number): ComboTier | null {
  if (combo >= 10) {
    return { milestone: 10, label: 'UNSTOPPABLE', hud: 'UNSTOP', mult: 3, color: PALETTE.hazard };
  }
  if (combo >= 7) {
    return { milestone: 7, label: 'MUNCHSTORM', hud: 'STORM', mult: 3, color: PALETTE.hazard };
  }
  if (combo >= 5) {
    return { milestone: 5, label: 'BRAIN RUSH', hud: 'RUSH', mult: 2, color: PALETTE.armed };
  }
  if (combo >= 3) {
    return { milestone: 3, label: 'HOT STREAK', hud: 'HOT', mult: 1.5, color: PALETTE.correct };
  }
  return null;
}

export function comboMultiplier(combo: number): number {
  return comboTier(combo)?.mult ?? 1;
}

/** Trim + ellipsize a label to at most n chars (used by the coach card / rule copy). */
export function compactLabel(s: string, n: number): string {
  const trimmed = s.trim();
  return trimmed.length > n ? `${trimmed.slice(0, Math.max(0, n - 3))}...` : trimmed;
}
